/**
 * @fileoverview Row selection hook for the Sales tables.
 * @description Keeps the ticked row ids of a list and prunes them against the rows now showing, so
 * the count, the header checkbox and whatever the selection is handed to (a bulk dialog) always
 * agree. A refetch that drops a row, or a row that stops being selectable, removes it from the
 * selection in the same render, and it is not silently re-selected if it returns later. A row id
 * that appears more than once (a list read page by page while rows move between pages) counts as
 * one row, so the pruning always settles instead of looping.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useState } from 'react'

export interface RowSelection<TRow> {
  /** The ticked rows that are still in the list, in list order. */
  selectedRows: TRow[]
  /** How many rows are ticked (always the length of `selectedRows`). */
  count: number
  /** Whether every selectable row is ticked (or, with a limit, as many as the limit allows). */
  allSelected: boolean
  /** Whether the limit on ticked rows has been reached (always false without a limit). */
  isFull: boolean
  /** Whether one row is ticked. */
  isSelected: (id: string) => boolean
  /** Ticks or unticks one row. */
  toggle: (id: string) => void
  /** Ticks every selectable row, or clears them when all are ticked. */
  toggleAll: () => void
  /** Unticks every row. */
  clear: () => void
}

/**
 * @description Keeps the first row of each id, in list order.
 * @param {TRow[]} rows - The rows, possibly with an id more than once.
 * @param {(row: TRow) => string} idOf - A row's id.
 * @returns {TRow[]} The rows with each id once.
 */
function uniqueBy<TRow>(rows: TRow[], idOf: (row: TRow) => string): TRow[] {
  const seen = new Set<string>()
  const unique: TRow[] = []
  for (const row of rows) {
    const id = idOf(row)
    if (!seen.has(id)) {
      seen.add(id)
      unique.push(row)
    }
  }
  return unique
}

/**
 * @description Tracks which of the given rows are ticked.
 * @param {TRow[]} rows - The rows that can be ticked, as they are showing now.
 * @param {(row: TRow) => string} idOf - A row's unique id.
 * @param {number} [maxSelected] - The most rows that may be ticked at once; ticking stops there.
 * @returns {RowSelection<TRow>} The ticked rows and how to change them.
 */
export function useRowSelection<TRow>(
  rows: TRow[],
  idOf: (row: TRow) => string,
  maxSelected: number = Number.POSITIVE_INFINITY,
): RowSelection<TRow> {
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set())
  const uniqueRows = uniqueBy(rows, idOf)
  const selectedRows: TRow[] = []
  for (const row of uniqueRows) {
    if (selected.has(idOf(row))) {
      selectedRows.push(row)
    }
  }

  // Prune while rendering (React's "adjust state when data changes" pattern, not an effect), so no
  // render ever shows a count that differs from the rows the selection resolves to.
  if (selectedRows.length !== selected.size) {
    setSelected(new Set(selectedRows.map(idOf)))
  }

  /**
   * @description Ticks or unticks one row, ignoring a tick once the limit is reached.
   * @param {string} id - The row's id.
   */
  const toggle = (id: string) => {
    setSelected(
      /**
       * @description Flips the row in a copy of the ticked ids.
       * @param {ReadonlySet<string>} previous - The ids ticked until now.
       * @returns {ReadonlySet<string>} The ids ticked after the click.
       */
      (previous) => {
        const next = new Set(previous)
        if (next.has(id)) next.delete(id)
        else if (next.size < maxSelected) next.add(id)
        return next
      },
    )
  }

  /**
   * @description Ticks every row (as many as the limit allows), or clears them when all are ticked.
   */
  const toggleAll = () => {
    const ticked = uniqueRows.slice(0, maxSelected)
    setSelected(
      /**
       * @description Clears the ticks when they already cover every tickable row, else ticks them.
       * @param {ReadonlySet<string>} previous - The ids ticked until now.
       * @returns {ReadonlySet<string>} The ids ticked after the click.
       */
      (previous) => (previous.size === ticked.length ? new Set() : new Set(ticked.map(idOf))),
    )
  }

  /**
   * @description Unticks every row.
   */
  const clear = () => setSelected(new Set())

  /**
   * @description Whether one row is ticked.
   * @param {string} id - The row's id.
   * @returns {boolean} True when the row is ticked.
   */
  const isSelected = (id: string) => selected.has(id)

  return {
    selectedRows,
    count: selectedRows.length,
    allSelected:
      selectedRows.length > 0 && selectedRows.length === Math.min(uniqueRows.length, maxSelected),
    isFull: selectedRows.length >= maxSelected,
    isSelected,
    toggle,
    toggleAll,
    clear,
  }
}
